import { SceneAudioConfig } from "../config/audio/types";
import { sceneComponentsBuild } from "../config/configHelpers";
import { FALLBACK_ASSETS_BASE_URL } from "../config/globals";
import { navGraphPruneNodes } from "../config/interactive/navGraphHelpers";
import { EntityState, SceneDataConfig } from "../config/types";
import { globalStateUrlApply } from "./utils/state";
import { isComponentDisabled } from "./utils/utils";
import { applyEntityStateToAudio } from "./utils/state";
import { eventAppError, eventSceneExit } from "./utils/gtag";

// TypeScript global declaration
declare const __DEV__: boolean;
const isDev = __DEV__;

export async function configureScene(sceneConfig: SceneDataConfig) {
	eventSceneExit(window.startSceneView);
	window.startSceneLoad = Date.now();
	const sceneData = applyState(sceneConfig, sceneConfig.state);
	const {
		interactiveComponents,
		navigateAfterVideo,
		audio,
		background: { src, videoName },
	} = sceneData;
	const backgroundPromise = resolveBackground(src);
	const componentData = sceneComponentsBuild(interactiveComponents);
	const componentsToRemove = componentData.filter(isComponentDisabled);

	const interactives = navGraphPruneNodes(componentData, componentsToRemove);

	const [backgroundData] = await Promise.all([backgroundPromise]);

	if (!backgroundData || !backgroundData.contentType || !backgroundData.data) {
		// TODO: error handling on background data failure
		console.error("early return ....");
		eventAppError(`Error fetching video ${src}: ${backgroundData} is invalid`, "video");
		return null;
	}

	const { contentType, data } = backgroundData;

	return {
		everything: sceneConfig,
		sceneInteractives: interactives,
		sceneName: videoName,
		audio: audio,
		state: sceneConfig.state,
		background: {
			navigateAfter: navigateAfterVideo ?? null,
			contentType: contentType,
			data: data,
			src: src,
		},
	};
}

/**
 * Apply entity state transformations to audio configuration
 */
function applyStateToAudio(
	states: readonly EntityState[],
	audio: SceneAudioConfig,
): SceneAudioConfig {
	let updatedAudio = { ...audio };

	for (const state of states) {
		updatedAudio = applyEntityStateToAudio(state, updatedAudio);
	}
	return updatedAudio;
}

/**
 * Apply entity state transformations to scene configuration
 */
function applyState(
	sceneConfig: SceneDataConfig,
	entityStates: SceneDataConfig["state"],
): SceneDataConfig {
	const stateList = entityStates.video.map((s) => s.value);

	return {
		...sceneConfig,
		audio: applyStateToAudio(entityStates.audio, sceneConfig.audio),
		background: {
			...sceneConfig.background,
			src: globalStateUrlApply(stateList, sceneConfig.background.src),
		},
	};
}

export async function resolveBackground(
	src: string,
): Promise<{ data: ArrayBuffer; contentType: string | null } | null> {
	const resolvedSrc = resolveMediaURL(src);
	return fetch(resolvedSrc, { cache: "force-cache" })
		.then(async (response) => {
			const blob = await response.blob();
			const buffer = await blob.arrayBuffer();

			return {
				data: buffer,
				contentType: response.headers.get("Content-Type"),
			};
		})
		.catch((error) => {
			console.error("Error fetching video:", src, error);
			eventAppError(`Error fetching video ${src}: ${error}`, "video");
			return null;
		});
}

export function resolveMediaURL(url: string): string {
	// window.__ENV__ vaules populated by env.js script generated via gitlab pipeline
	const win = window as Window & { __ENV__?: { ASSET_BASE_URL?: string } };
	const baseURL = isDev ? "/assets" : (win?.__ENV__?.ASSET_BASE_URL ?? FALLBACK_ASSETS_BASE_URL);
	return `${baseURL}/${url}`;
}
